import React, { createContext, useContext, useEffect, useState } from 'react';
import httpClient from '../http/client';
import {
  isAuthenticated,
  clearStoredUser,
  storeUser,
  isAuthorized,
  getStoredUser,
  normalizeLoginPayload,
  buildStoredUserMeta,
} from '../auth';
import {
  isGlobalAcl,
  loadOperationsList,
  orgIdsFromAcl,
  persistOperationsList,
} from '../auth/operations';
import {
  expireSessionIfStale,
  hasPersistedAuthState,
  startSessionWatch,
} from '../auth/session';

const TENANT_API_URL = process.env.REACT_APP_TENANT_API_URL;
const AUTH_API_URL = process.env.REACT_APP_AUTH_API_URL;

const UserContext = createContext(null);

const hydrateUserFromStorage = () => {
  const stored = getStoredUser();
  if (!stored.user) return null;
  return {
    ...(stored.meta ?? {}),
    user: stored.user,
    success: true,
  };
};

const initialOperationsList = () => loadOperationsList();

const initialStep = () => {
  const list = loadOperationsList();
  if (isAuthenticated() && list.length > 1) return 'select';
  return 'login';
};

export const UserProvider = ({ children }) => {
  const staleSession = expireSessionIfStale();

  const [user, setUser] = useState(hydrateUserFromStorage);
  const [operationsList, setOperationsList] = useState(initialOperationsList);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(initialStep);
  const [authSuccess, setAuthSuccess] = useState(undefined);

  useEffect(() => {
    let cancelled = false;

    if (hasPersistedAuthState()) {
      startSessionWatch();
    }

    const hydrateOperations = async () => {
      if (!isAuthenticated()) return;

      let list = loadOperationsList();
      const { acl } = getStoredUser();

      if (list.length === 0) {
        if (isGlobalAcl(acl)) {
          try {
            const tenantsRes = await httpClient.get(
              `${TENANT_API_URL}/tenants/actives?name=false`,
            );
            list = tenantsRes.data ?? [];
          } catch {
            list = [];
          }
        } else {
          list = orgIdsFromAcl(acl);
        }

        if (list.length > 0 && !cancelled) {
          persistOperationsList(list);
          setOperationsList(list);
        }
      }

      if (!cancelled && list.length > 1) {
        setStep(current => (current === 'login' ? 'select' : current));
      }
    };

    hydrateOperations();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (username, password) => {
    if (isAuthenticated()) clearStoredUser();

    setLoading(true);
    try {
      const response = await httpClient.post(`${AUTH_API_URL}/login`, {
        username,
        password,
      });
      const data = response.data;
      const normalized = normalizeLoginPayload(data);

      if (!normalized) {
        setAuthSuccess(false);
        setStep('login');
        setLoading(false);
        return new Error('Credenciais inválidas.');
      }

      const meta = buildStoredUserMeta(normalized);

      storeUser({
        token: normalized.accessToken,
        refreshToken: normalized.refreshToken,
        refreshExpiresIn: normalized.refreshExpiresIn,
        user: username,
        acl: normalized.acl ?? null,
        meta,
      });
      setUser({ ...data, user: username, token: normalized.accessToken });

      const { acl } = getStoredUser();
      let allowedOrganizations;

      if (isGlobalAcl(acl)) {
        const tenantsRes = await httpClient.get(
          `${TENANT_API_URL}/tenants/actives?name=false`,
        );
        allowedOrganizations = tenantsRes.data ?? [];
      } else {
        allowedOrganizations = orgIdsFromAcl(acl);
      }

      persistOperationsList(allowedOrganizations);
      setOperationsList(allowedOrganizations);

      if (allowedOrganizations.length === 1) {
        setAuthSuccess(true);
        setLoading(false);
        return allowedOrganizations[0];
      }

      setAuthSuccess(true);
      setStep('select');
      setLoading(false);
      return null;
    } catch (err) {
      setAuthSuccess(false);
      setStep('login');
      setLoading(false);
      return err instanceof Error ? err : new Error('Erro de autenticação.');
    }
  };

  const destroyAuthenticated = () => clearStoredUser();
  const refreshAuth = () => isAuthenticated();

  if (staleSession) return null;

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        authSuccess,
        setAuthSuccess,
        isAuthenticated,
        isAuthorized,
        login,
        destroyAuthenticated,
        refreshAuth,
        operationsList,
        loading,
        step,
        setStep,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
